import api from '../index'

export type AiProvider
  = | 'qwen'
    | 'deepseek'
    | 'kimi'
    | 'openai'
    | 'claude'
    | 'gemini'
    | 'ernie'
    | 'zhipu'
    | 'doubao'
    | 'ollama'

export type EnabledStatus = 0 | 1

export interface AiConfigOptions {
  supports_tools: boolean
  supports_vision: boolean
  local: boolean
  cost_per_1k_tokens: number
  description: string | null
}

export interface AiConfig {
  id: number
  uid: number
  provider: AiProvider
  model_name: string
  api_key?: string | null
  has_api_key?: boolean
  api_endpoint: string | null
  is_default: EnabledStatus
  is_enabled: EnabledStatus
  priority: number
  max_tokens: number
  temperature: number
  config: AiConfigOptions | null
  created_at: string
  updated_at: string
  deleted_at: string | null
}

export interface AiConfigListParams {
  uid?: number
  provider?: AiProvider
  is_enabled?: EnabledStatus
  page_type?: 'page' | 'all'
  page_no?: number
  page_size?: number
}

export interface AiConfigListPage {
  list: AiConfig[]
  total: number
  page_no: number
  page_size: number
}

export interface AiConfigTestResult {
  success: boolean
  message: string
  provider: AiProvider
  model_name: string
  response_time: number
  test_response?: string
}

export interface AiConfigCreateData {
  uid?: number
  provider: AiProvider
  model_name: string
  api_key?: string
  api_endpoint?: string
  is_default?: EnabledStatus
  priority?: number
  max_tokens?: number
  temperature?: number
  config?: AiConfigOptions
}

export interface AiConfigUpdateData {
  provider?: AiProvider
  model_name?: string
  api_key?: string | null
  api_endpoint?: string
  is_default?: EnabledStatus
  is_enabled?: EnabledStatus
  priority?: number
  max_tokens?: number
  temperature?: number
  config?: AiConfigOptions
}

export interface ModelPriceCreateData {
  provider: AiProvider
  model_name: string
  input_credits_per_1k: number
  output_credits_per_1k: number
  cached_input_credits_per_1k?: number
  input_cost_per_1m?: number
  output_cost_per_1m?: number
  cached_input_cost_per_1m?: number
  currency?: string
  effective_from: string
  effective_to?: string | null
  is_enabled?: EnabledStatus
}

export interface ModelPrice {
  id: number
  provider: AiProvider
  model_name: string
  input_credits_per_1k: string
  output_credits_per_1k: string
  cached_input_credits_per_1k: string
  input_cost_per_1m: string
  output_cost_per_1m: string
  cached_input_cost_per_1m: string
  currency: string
  effective_from: string
  effective_to: string | null
  is_enabled: EnabledStatus
  created_at: string
  updated_at: string
}

export interface ModelPriceListParams {
  provider?: AiProvider
  model_name?: string
  page_no?: number
  page_size?: number
}

export interface ModelPriceListPage {
  list: ModelPrice[]
  total: number
  page_no: number
  page_size: number
}

export interface ModelPriceUpdateData {
  effective_to?: string | null
  is_enabled?: EnabledStatus
}

export const aiConfigApi = {
  // 获取 AI 配置列表
  list: (params?: AiConfigListParams) => api.get('ai/config/list', { params }),

  // 获取 AI 配置详情
  detail: (id: number) => api.get(`ai/config/${id}`),

  // 创建 AI 配置
  create: (data: AiConfigCreateData) => api.post('ai/config/add', data),

  // 更新 AI 配置
  update: (id: number, data: AiConfigUpdateData) => api.put(`ai/config/${id}`, data),

  // 删除 AI 配置
  delete: (id: number) => api.delete(`ai/config/${id}`),

  // 测试 AI 配置连通性
  test: (id: number) => api.post<AiConfigTestResult>(`ai/config/${id}/test`),

  // 获取模型计费版本列表
  listModelPrices: (params?: ModelPriceListParams) => api.get<ModelPriceListPage>('ai/billing/model-prices', { params }),

  // 新增模型计费版本
  createModelPrice: (data: ModelPriceCreateData) => api.post('ai/billing/model-prices', data),

  // 模型价格只允许结束版本或切换启用状态
  updateModelPrice: (id: number, data: ModelPriceUpdateData) => api.put(`ai/billing/model-prices/${id}`, data),
}

export default aiConfigApi
